import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { useClient, useQuery } from 'jsonapi-react';
import { CourseMonitoringModule } from './course-monitoring-module';
import { CourseMonitoringLogActivity } from './course-monitoring-log-activity';
import TaskWidget from './TaskWidget';
import EventForm from '@/components/custom/schedule-modal';
import { CourseMonitoringLogImportant } from './course-monitoring-log-important';
import { t } from 'i18next';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { Card, CardContent, CardProgressWrapper } from '@/components/ui/card';
import { CourseBanner, CourseBannerContainer, CourseBannerContent, CourseBannerCTA, CourseBannerTitle } from '@/components/custom/course';
// import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader } from '@/components/ui/sheet';

export function CourseMonitoring(props) {
  const { uri } = props;

  const client = useClient();
  const courseId = parseInt(useParams().id);
  const { abilities } = usePlatformAbilities();
  const { data: course, isLoading: isLoadingCourse } = useQuery(`/courses/${courseId}?detailed=true`);

  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState();
  const [showFormEvent, setShowFormEvent] = useState(false);
  const [selectedTab, setSelectedTab] = useState('atualizacoes');

  const parsedCourse = { ...course, 'learning-systems': course?.['learning-systems']?.sort((a, b) => a.order - b.order) };

  const fetchEvents = async initDate => {
    try {
      const initialDate = moment(initDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(initDate).endOf('month').format('YYYY-MM-DD');
      const courseFilter = courseId ? `&filter[course_id]=${courseId}` : '';
      const { data } = await client.fetch(`events?filter[date_between][initial]=${initialDate}&filter[date_between][final]=${endDate}${courseFilter}`);
      setEvents(data);
    } catch (e) {
      console.log(e);
    }
  };

  const handleDownloadCertificate = () => {
    window.open(`${process.env.REACT_APP_DEV_URL}//user_certificates/download/${parsedCourse?.certificate?.hash_id}.pdf`, '_blank');
  };

  useEffect(() => {
    fetchEvents();
  }, [courseId]);

  const closeFormEvent = () => {
    setShowFormEvent(false);
    setEditingEvent(null);
  };

  const courseProgress = Number(parsedCourse?.progress?.toFixed(2));

  const courseIsStarted = courseProgress > 0;

  const courseButtonLink = () => {
    if (courseProgress === 100) {
      const lastModule = parsedCourse?.['learning-systems']?.length - 1;
      return `/curso/${parsedCourse?.id}/modulo/${parsedCourse?.['learning-systems']?.[lastModule]?.id}/sobre`;
    } else if (courseProgress === 0) {
      const firstModule = parsedCourse?.['learning-systems']?.[0]?.id;
      return `/curso/${parsedCourse?.id}/modulo/${firstModule}/sobre`;
    } else {
      const nextModule = parsedCourse?.['learning-systems']?.find(item => item.progress < 100);
      return `/show-course/${nextModule?.id}/classroom`;
    }
  };

  const totalLessons = () => {
    const parsedItems = course?.['learning-systems']?.map(item => item?.learning_system_items_total_count);

    const totalLessons = parsedItems?.reduce((sum, curr) => {
      return sum + curr;
    }, 0);

    return totalLessons;
  };

  const hasModuleAndLesson = course?.['learning-systems']?.length > 0 && totalLessons() > 0;

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: '/curso',
        name: 'Cursos',
        isActive: false
      },
      {
        route: uri,
        name: parsedCourse?.title,
        'official-content': parsedCourse?.['official-content'] ?? false,
        isActive: true
      }
    ]
  };

  const modules = course?.['learning-systems'];

  const checkOpenModulesFinished = () => {
    const openModules = modules?.filter(module => {
      const moduleRequirements = module.learning_system_requirement_ids;
      const requiredModules = modules.filter(item => moduleRequirements?.includes(item.id));
      if (moduleRequirements.length === 0 || requiredModules?.every(item => item.finished === true)) {
        return module;
      }
    });
    const openModulesFinished = openModules?.every(item => item.progress === 100);

    return openModulesFinished;
  };

  if (isLoadingCourse) {
    return (
      <DashboardContent>
        <Loader />
      </DashboardContent>
    );
  }

  return (
    <DashboardContent
      breadcrumbs={breadcrumbs}
      variant="plain"
    >
      <Card className="mb-6">
        <CardContent className="w-full flex-grow-0">
          <CourseBanner
            backgroundUrl={parsedCourse?.image?.url}
            className="min-h-[224px]"
          >
            <CourseBannerContent>
              <CourseBannerContainer>
                {/* <Badge
                  variant="primary-light"
                  size="xs"
                >
                  {parsedCourse?.knowledge}
                </Badge> */}

                <CourseBannerTitle>{parsedCourse?.title}</CourseBannerTitle>

                {courseIsStarted && (
                  <CardProgressWrapper
                    variant="cover"
                    toFixed={1}
                    percentage={courseProgress}
                  />
                )}

                <CourseBannerCTA>
                  {hasModuleAndLesson ? (
                    <Button
                      wide
                      disabled={checkOpenModulesFinished()}
                      asChild
                    >
                      <Link to={courseButtonLink()}>{courseIsStarted ? 'Continuar onde parei' : 'Começar'}</Link>
                    </Button>
                  ) : (
                    <p className="mb-0">Este curso estará disponível em breve.</p>
                  )}

                  {parsedCourse?.certificate?.hash_id && (
                    <Button
                      variant="outline"
                      wide
                      onClick={handleDownloadCertificate}
                      style={{ display: 'none' }}
                    >
                      {t('button.certification')}
                    </Button>
                  )}
                </CourseBannerCTA>
              </CourseBannerContainer>
            </CourseBannerContent>
          </CourseBanner>
        </CardContent>
      </Card>

      <Tabs
        defaultValue={selectedTab}
        value={selectedTab}
        onValueChange={setSelectedTab}
      >
        {courseIsStarted && (
          <TabsList className="mb-6">
            <TabsTrigger value="atualizacoes">Atualizações</TabsTrigger>
            <TabsTrigger value="sobre">Sobre</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value={selectedTab}>
          {courseIsStarted && selectedTab === 'atualizacoes' && (
            <Page className="animate-fade-in">
              <PageContent>
                <CourseMonitoringLogImportant />

                <CourseMonitoringLogActivity
                  useType={1}
                  courseId={courseId}
                  title="Últimas atualizações do curso"
                />
              </PageContent>

              <PageSidebar>
                {abilities?.tarefas && <TaskWidget currentCourse={parsedCourse} />}

                {/* <DashboardSidebar
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                onActiveStartDateChange={onActiveStartDateChange}
                eventDates={eventDates}
                setShowFormEvent={setShowFormEvent}
                events={events}
                /> */}
              </PageSidebar>
            </Page>
          )}

          {!courseIsStarted ? (
            <CourseMonitoringModule course={parsedCourse} />
          ) : (
            selectedTab === 'sobre' && (
              <div className="animate-fade-in">
                <CourseMonitoringModule course={parsedCourse} />
              </div>
            )
          )}
        </TabsContent>
      </Tabs>

      <Sheet
        show={showFormEvent}
        onClose={closeFormEvent}
      >
        <SheetContent>
          <SheetHeader />
          <SheetBody>
            <EventForm
              onClose={() => setShowFormEvent(false)}
              editingEvent={editingEvent}
              events={events}
              setEvents={setEvents}
            />
          </SheetBody>
          <SheetFooter />
        </SheetContent>
      </Sheet>
    </DashboardContent>
  );
}
